// Logo Ticker Infinite Carousel
class LogoTicker {
    constructor() {
        this.ticker = document.querySelector('.logo-ticker');
        this.logosContainer = document.querySelector('.logo-ticker__logos');
        this.logos = document.querySelectorAll('.logo-ticker__logo');
        this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        this.resizeTimeout = null;
        
        if (!this.ticker || !this.logosContainer) return;
        
        this.init();
        this.setupResizeHandler();
    }
    
    init() {
        // Wait for all images to load before initializing
        this.waitForImagesToLoad().then(() => {
            // Clone logos for seamless infinite scroll
            this.cloneLogos();
            
            // Wait for clones to be laid out, then set up animation
            // Use multiple requestAnimationFrame to ensure layout is complete
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.setupAnimation();
                });
            });
            
            // Start the animation
            this.startAnimation();
        });
    }
    
    waitForImagesToLoad() {
        // Wait for all logo images to be fully loaded
        const imagePromises = Array.from(this.logos).map(logo => {
            return new Promise((resolve) => {
                if (logo.complete) {
                    resolve();
                } else {
                    logo.addEventListener('load', resolve, { once: true });
                    logo.addEventListener('error', resolve, { once: true });
                    // Timeout after 5 seconds to prevent indefinite waiting
                    setTimeout(resolve, 5000);
                }
            });
        });
        
        return Promise.all(imagePromises);
    }
    
    cloneLogos() {
        // Clone all logos multiple times to create seamless loop across full width
        const cloneCount = 3; // Clone 3 times for better fill
        for (let i = 0; i < cloneCount; i++) {
            this.logos.forEach(logo => {
                const clone = logo.cloneNode(true);
                clone.classList.add('logo-ticker__logo--clone');
                this.logosContainer.appendChild(clone);
            });
        }
    }
    
    getGapSize() {
        // Get responsive gap size based on viewport width
        const width = window.innerWidth;
        if (width <= 550) {
            return 32; // 2rem = 32px
        } else if (width <= 768) {
            return 48; // 3rem = 48px
        } else {
            return 144; // 9rem = 144px
        }
    }
    
    setupAnimation() {
        // Wait for next frame to ensure all elements are rendered
        requestAnimationFrame(() => {
            // Get the exact width of one complete set of logos
            // by measuring from the first logo to the end of the last original logo
            let totalWidth = 0;
            
            if (this.logos.length > 0) {
                // Get bounding rect of first logo
                const firstRect = this.logos[0].getBoundingClientRect();
                // Get bounding rect of last logo
                const lastRect = this.logos[this.logos.length - 1].getBoundingClientRect();
                
                // Calculate total width: from left edge of first to right edge of last
                // This already includes the CSS gaps between logos
                totalWidth = lastRect.right - firstRect.left;
            }
            
            // Fallback if calculation fails
            if (totalWidth <= 0) {
                totalWidth = this.logos.length * 250; // Fallback estimation
            }
            
            if (this.isIOS) {
                // Use JavaScript animation for iOS
                this.setupIOSAnimation(totalWidth);
            } else {
                // Use CSS animation for other browsers
                // Round to integer for pixel-perfect rendering
                const animationDistance = Math.round(-totalWidth);
                this.logosContainer.style.setProperty('--animation-distance', `${animationDistance}px`);
                this.logosContainer.classList.add('logo-ticker__logos--animate');
            }
        });
    }
    
    getAnimationDuration() {
        // Get responsive animation duration based on viewport width
        const width = window.innerWidth;
        if (width <= 550) {
            return 28000; // 28 seconds
        } else if (width <= 768) {
            return 35000; // 35 seconds
        } else {
            return 50000; // 50 seconds
        }
    }
    
    setupIOSAnimation(totalWidth) {
        let startTime = null;
        const duration = this.getAnimationDuration();
        let animationId;
        
        const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const elapsed = currentTime - startTime;
            const progress = (elapsed % duration) / duration;
            
            // Calculate position based on progress
            const translateX = -(progress * totalWidth);
            this.logosContainer.style.transform = `translateX(${translateX}px)`;
            
            animationId = requestAnimationFrame(animate);
        };
        
        // Start the animation
        animationId = requestAnimationFrame(animate);
        
        // Store animation ID for potential pause/resume
        this.iosAnimationId = animationId;
    }
    
    startAnimation() {
        // Animation is handled by CSS
        // This method can be used for additional functionality

    }
    
    pause() {
        if (this.isIOS && this.iosAnimationId) {
            cancelAnimationFrame(this.iosAnimationId);
        } else {
            this.logosContainer.style.animationPlayState = 'paused';
        }
    }
    
    resume() {
        if (this.isIOS) {
            // Recalculate width for iOS
            let totalWidth = 0;
            
            if (this.logos.length > 0) {
                const firstRect = this.logos[0].getBoundingClientRect();
                const lastRect = this.logos[this.logos.length - 1].getBoundingClientRect();
                totalWidth = lastRect.right - firstRect.left;
            }
            
            // Fallback if calculation fails
            if (totalWidth <= 0) {
                totalWidth = this.logos.length * 250;
            }
            
            this.setupIOSAnimation(totalWidth);
        } else {
            this.logosContainer.style.animationPlayState = 'running';
        }
    }
    
    setSpeed(speed) {
        // Speed in seconds (lower = faster)
        this.logosContainer.style.animationDuration = `${speed}s`;
    }
    
    setupResizeHandler() {
        // Debounced resize handler to recalculate on viewport change
        window.addEventListener('resize', () => {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => {
                // Recalculate animation if CSS animation is being used
                if (!this.isIOS && this.logosContainer.classList.contains('logo-ticker__logos--animate')) {
                    this.setupAnimation();
                }
            }, 250);
        });
    }
}

// Initialize logo ticker when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new LogoTicker();
});

// // Pause on hover (optional)
// document.addEventListener('DOMContentLoaded', () => {
//     const ticker = document.querySelector('.logo-ticker');
//     if (ticker) {
//         ticker.addEventListener('mouseenter', () => {
//             const logosContainer = ticker.querySelector('.logo-ticker__logos');
//             if (logosContainer) {
//                 logosContainer.style.animationPlayState = 'paused';
//             }
//         });
        
//         ticker.addEventListener('mouseleave', () => {
//             const logosContainer = ticker.querySelector('.logo-ticker__logos');
//             if (logosContainer) {
//                 logosContainer.style.animationPlayState = 'running';
//             }
//         });
//     }
// }); 